import React from 'react';
import { AlertTriangle, Compass, TrendingDown, Users } from 'lucide-react';

/**
 * ProvenanceBadges — renders a compact row of tone-coded badges
 * showing why a scenario or business case was modelled. Mirrors
 * the narrative bridges from SolutionFinderContextStack but
 * in a smaller, list-friendly form factor for tile/row use.
 *
 * Pass `provenance` (the dict persisted at save time) and an
 * optional `size` of "sm" (default) or "xs" for tighter layouts.
 * Returns null when no bridges were active at save time.
 */
export default function ProvenanceBadges({ provenance, size = 'sm', testIdPrefix = 'provenance' }) {
  if (!provenance) return null;
  const { constraint, nav_condition, industry_kpi, pe_benchmarking, kpi_alignment } = provenance;
  if (!constraint && !nav_condition && !industry_kpi && !pe_benchmarking && !kpi_alignment) return null;

  const sizing = size === 'xs'
    ? 'px-1.5 py-0.5 text-[9px] gap-1'
    : 'px-2 py-0.5 text-[10px] gap-1.5';
  const iconSize = size === 'xs' ? 9 : 11;

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-badges`}>
      {constraint?.label && (
        <span
          className={`inline-flex items-center rounded-full border border-orange-200 bg-orange-50 text-orange-800 font-medium ${sizing}`}
          title={`Modelled against the Mission Control constraint "${constraint.label}" (−${constraint.leak}% leak)`}
          data-testid={`${testIdPrefix}-constraint`}
        >
          <AlertTriangle size={iconSize} className="text-orange-600" />
          From Mission Control · {constraint.label}
        </span>
      )}
      {nav_condition?.label && (
        <span
          className={`inline-flex items-center rounded-full border border-teal-200 bg-teal-50 text-teal-800 font-medium ${sizing}`}
          title={`Modelled to close a ${nav_condition.label} gap identified on Org Benchmarks`}
          data-testid={`${testIdPrefix}-nav-condition`}
        >
          <Compass size={iconSize} className="text-teal-600" />
          From Org Benchmarks · {nav_condition.label}
          {nav_condition.gap != null && Number.isFinite(nav_condition.gap) && (
            <span className="opacity-70">{` (+${nav_condition.gap} pp)`}</span>
          )}
        </span>
      )}
      {pe_benchmarking?.condition && (
        <span
          className={`inline-flex items-center rounded-full border border-violet-200 bg-violet-50 text-violet-800 font-medium ${sizing}`}
          title={`Modelled to strengthen ${pe_benchmarking.condition} — the workforce condition flagged on PE Benchmarking`}
          data-testid={`${testIdPrefix}-pe-benchmarking`}
        >
          <Users size={iconSize} className="text-violet-600" />
          From PE Benchmarking · {pe_benchmarking.condition}
        </span>
      )}
      {industry_kpi?.kpi_name && (
        <span
          className={`inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 text-indigo-800 font-medium ${sizing}`}
          title={`Modelled to close an Industry Benchmarks gap on ${industry_kpi.kpi_name}`}
          data-testid={`${testIdPrefix}-industry-kpi`}
        >
          <TrendingDown size={iconSize} className="text-indigo-600" />
          From Industry Benchmarks · {industry_kpi.kpi_name}
        </span>
      )}
      {kpi_alignment?.kpi_name && (
        <span
          className={`inline-flex items-center rounded-full border border-rose-200 bg-rose-50 text-rose-800 font-medium ${sizing}`}
          title={
            kpi_alignment.expected != null && kpi_alignment.observed != null
              ? `Modelled to close a transfer gap: ${kpi_alignment.measure_label || 'a learning measure'} → ${kpi_alignment.kpi_name} (${kpi_alignment.expected}% expected vs ${kpi_alignment.observed}% realised)`
              : `Modelled to close a KPI Alignment transfer gap on ${kpi_alignment.kpi_name}`
          }
          data-testid={`${testIdPrefix}-kpi-alignment`}
        >
          <TrendingDown size={iconSize} className="text-rose-600" />
          From KPI Alignment · {kpi_alignment.measure_label ? `${kpi_alignment.measure_label} → ` : ''}{kpi_alignment.kpi_name}
          {kpi_alignment.gap != null && Number.isFinite(kpi_alignment.gap) && (
            <span className="opacity-70">{` (Δ ${kpi_alignment.gap}pp)`}</span>
          )}
        </span>
      )}
    </div>
  );
}
